import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import track from "../../../../utils/track";
import generalService from "../../../../service/general-service";
import { startTeaser } from "../baiye-teaser-cache";
import PaymentService from "../../../../service/payment-service";
import InvoiceSheet from "../../../dialog/invoice-sheet/invoice-sheet";
import {
  openEcpayWindow,
  submitToEcpay,
} from "../../../../utils/ecpay-redirect";
import BaiyeStoryIntro from "./baiye-story-intro";
import PurchaseSheet from "../../../dialog/purchase-sheet/purchase-sheet";
import "./baiye-story-style.scss";

//—— 白夜・八字批命：解命後的故事頁 ——
//
//  流程上接在 bazi-baiye-page 的最後一幕（reading）之後：
//  解命影片播完 → 導到這裡。
//
//  和前面那一頁的差別是「版型」而不只是內容：
//  劇情頁是滿版固定高度、一次只給一幕、不能捲；這一頁是一路捲到底的長頁。
//  兩種版型塞進同一個元件會變成一堆 if，所以拆成兩支。
//
//  內容全在下面 STORY 這張表裡，元件只負責排版 ——
//  改文案、加段落、調順序都只動那張表，作法同劇情頁的 SCENES。

//回上一頁（劇情頁）的去處。直接貼網址進來時沒有上一頁，要有個退路。
const BACK_TO = "/product/bazi-baiye";

//—— 開發用的替身問診資料 ——
//  location state 不進網址，也撐不過重新整理 —— 直接開這一頁或按 F5，
//  intake 就是空的，頁面會把人導回劇情頁。
//
//  但這一頁的版型要反覆調（圖、對話框位置、字級都得看著畫面改），
//  每改一次就從問診走十題不切實際。開發時用一組固定的假資料頂著，
//  網址列直接打 /product/bazi-baiye/story 就能看到完整畫面。
//
//  只在 npm start 生效：NODE_ENV 在建置時就會被換成字面值 "production"，
//  所以正式 build 裡這個常數是 null，整段假資料也會被搖掉，不會進 bundle。
//
//  生日刻意挑一個「有時辰、有出生地」的：這樣真太陽時校正那條路徑
//  平常就會被走到，不會等到上線才第一次跑。
//底部按鈕的觸發點：使用者滑到這個元素，按鈕就出現，之後不再消失。
//  放在「是為了那件事來的吧？」那一行 —— 在那之前他還在鋪陳，
//  按鈕太早出現會變成在打斷故事。
const CTA_TRIGGER_ID = "baiye-story-cta-trigger";

//付款開在新分頁之後，這一頁靠輪詢知道結果（見 watchOrder）。
//  3 秒一次：使用者在綠界刷完卡大概會停留幾秒看成功畫面，
//  這個間隔讓他切回來時多半已經變了，又不至於打太兇。
//  15 分鐘封頂：綠界那張單本來就會過期，再等下去沒有意義。
const PAY_POLL_MS = 3000;
const PAY_POLL_LIMIT_MS = 15 * 60 * 1000;

//—— 購買方案 ——
//
const SAMPLE_INTAKE = {
  relation: "self",
  name: "王小明",
  gender: "male",
  birthDate: "1995/12/08",
  birthTime: "16:20",
  birthPlace: { label: "臺灣 臺北市", lat: 25.033, lng: 121.5654 },
  orientation: "hetero",
  relationship: "single",
  career: "employed",
  concern: "",
  email: "sample@example.com",
};

//這一頁在什麼情況下可以用範例資料顯示：
//
//  開發時（npm start）—— 版型要反覆調，每次從問診走十題不實際。
//  網址帶 ?preview  —— 給人看用的。location.state 不進網址，
//                      所以把連結傳給別人時對方一定沒有問診資料。
//
//  預覽模式刻意做成「網址帶參數」而不是另開一條路由：
//  它走的是同一支元件、同一組 block，看到的就是真的那一頁，
//  不會出現「預覽版跟正式版長得不一樣」這種事。
//  刻意不取 use 開頭的名字：那會被 ESLint 的 react-hooks 規則
//  當成 Hook，而它是在三元運算式裡呼叫的，會被判定成「條件式呼叫 Hook」。
const wantsSampleIntake = (search) =>
  process.env.NODE_ENV === "development" ||
  new URLSearchParams(search).has("preview");

//—— 問診資料 ——
//
//  劇情頁走完會把答案放在 location state 的 intake 裡帶過來（見 bazi-baiye-page
//  最後一幕的導頁）。欄位名就是 SCENES 裡的 field，值也沒有轉換過：
//
//    relation      看誰的八字   self / family / partner / friend / other
//    birthDate     出生年月日   "1990/01/01"（國曆）
//    birthTime     出生時間     "12:30"，或 "unknown"
//    birthPlace    出生地       BirthplaceField 給的物件（含經緯度）
//    gender        性別         "male" / "female"（排盤要的 "1"/"0" 之後再轉）
//    name          稱呼
//    orientation   取向         hetero / lgbt
//    relationship  感情狀態     single / crush / dating / breakup / married
//    career        工作狀態     student / seeking / employed / business / freelance / none
//    concern       想問的煩惱   可留白
//    email         通知信箱
//
//  重新整理就會沒有 —— location state 不進網址也不落地。
//  所以沒接到資料時不硬撐著畫面，直接把人送回劇情頁從頭走一次。

//—— 故事內容 ——
//
//  整頁是一條長捲軸，由一個個 block 串起來，中間沒有間隙也沒有頁的概念。
//  要加內容就往這張表後面加一筆，版型不用動。
//
//  目前有四種 block：
//    image  滿版圖片，高度依原圖比例，接縫處對齊沒有空隙；
//           可疊任意顆對話框（bubbles）或字幕（captions）
//    text   黑底文字，一行一句（lines 給幾行就幾行）；
//           帶 size: "lg" 的話整段字一起放大，用在要壓過周圍的段落；
//           帶 head / tail 的話上緣／下緣多留一截，用來跟前後拉開距離
//    panel  花紋背景上嵌一塊黑板子，寫標題 + 大段內文（可接動態資料）
//    chart  四柱命盤表（十神／天干／地支／藏干），資料全部來自後端
//    chapter 章節目錄卡：膠囊標籤 + 標題 + 條列，字直接壓在花紋上
//    reviews 用戶回饋列表：遮罩信箱 + 星等 + 評語
//
//  之後要新的樣式（例如兩欄、或圖上疊字），就多一種 type，
//  在底下的 renderBlock 加一個分支 —— 不要把版型判斷寫進資料表裡。
const IMG = `${process.env.PUBLIC_URL}/images/baiye/`;

const BLOCKS = [
  //1. 白夜持扇
  { type: "image", src: `${IMG}baiye-story-1.png` },

  //2. 承上啟下的一句話
  //   給字串就是一般白字，給物件才帶樣式
  {
    type: "text",
    lines: ["現在", { text: "來看看你的命局吧", style: "gradient" }],
  },

  //3. 白夜沉吟。對話框疊在圖上 ——
  //   bubble 的位置與大小都用百分比，圖縮放時框會跟著跑，不會脫節。
  {
    type: "image",
    src: `${IMG}baiye-story-2.jpg`,
    bubbles: [{ text: "嗯…", top: "18%", left: "11%", width: "24%" }],
  },

  //4. 白夜開口。前兩句是鋪陳，「甜言蜜語」才是重音 ——
  //   gap 讓它和上面空開一段，青綠的光跟前面的藍區分開：
  //   藍是「帶你往下看」，綠是他自己在說話。
  //
  //   size: "lg" 把整段一起放大：這是他第一次開口，份量要壓過前面那句引導。
  {
    type: "text",
    size: "lg",
    lines: [
      "我這折扇一收",
      "講的可都不是討喜的",
      { text: "甜言蜜語", style: "jade", gap: true },
    ],
  },

  //5. 白夜端詳生辰。框裡寫的是使用者自己的四柱 ——
  //   這是整頁第一次出現「他的」東西，前面都還是講給任何人聽的開場。
  //
  //   bubble.from 而不是 bubble.text：內容要等排盤回來才有，
  //   但「框長什麼樣、擺在哪裡」仍然是這張表的事（見 renderBlock 的 data）。
  {
    type: "image",
    src: `${IMG}baiye-story-3.png`,
    bubbles: [
      {
        from: "pillars",
        //框靠右下角擺。四個數字都是「相對整張圖」的百分比：
        //  left/width 相對圖寬，top 相對圖高（absolute 的百分比就是這樣算的）。
        //
        //  right  = 54 + 42 = 96%  → 右邊留 4% 的邊
        //  bottom = 60.5 + 32.5 = 93%  → 下面留 7% 的邊
        //  （框高 = 框寬 ÷ aspect = 42% ÷ 0.72 = 58.3% 的「圖寬」，
        //    換算成圖高的百分比要再乘上圖的長寬比 0.558，得 32.5%）
        //
        //  要整體縮放就只改 width，然後把 left/top 依上面兩條式子反推 ——
        //  字級是「框寬 × ratio」，會自己跟著縮，不必另外調。
        top: "60.5%",
        left: "54%",
        width: "42%",
        //字相對框的比例。整體大小交給 width 管，這個只管「字在框裡佔多滿」。
        ratio: 0.17,
        //0.72 是量設計稿得到的（367 x 515px）
        aspect: "0.72",
        //這顆的黑字外圍有一圈青藍光（量到 ΔR −19 / ΔG 0 / ΔB −5）
        glow: true,
      },
    ],
  },

  //6. 叫出對方的名字。整頁到這裡才第一次指名道姓 ——
  //   前面都還是「你」，這一刻他抬頭看著你說出你的名字。
  //   所以這一塊只有一行、字大、四周空，不放任何別的東西。
  {
    type: "text",
    //tail：名字唸完要留一段空白，下一塊的眼睛才不會馬上壓上來
    tail: true,
    lines: [{ from: "name", style: "name" }],
  },

  //7. 抬眼。只有一雙眼睛的橫幅特寫 ——
  //   名字被叫出來之後，接的是他看過來的那一眼，不需要任何文字。
  //   這張是橫式（比例 1.91），在滿版的直欄裡只佔一條窄帶，
  //   上下的淡出會讓它像從黑裡浮出來又沉回去。
  { type: "image", src: `${IMG}baiye-story-4.jpg` },

  //8. 他開口下判斷。三句都用漸層字（同 block 2 的「來看看你的命局吧」）——
  //   那是「帶你往下看」的語氣，跟 block 4 青綠發光的自嘲不是同一種。
  //
  //   中間夾一條直線：上下是同一個人連著講的話，他只是頓了一下，
  //   所以不能用橫線（那會切成兩個段落）。
  {
    type: "text",
    //sm：這幾句比前面長（一行十一個字），用轉折點的字級會擠到貼邊
    size: "sm",
    lines: [
      { text: "你這命局裡的煞氣與靈光", style: "gradient" },
      { text: "我一眼便瞧個透徹", style: "gradient" },
      { divider: true },
      {
        text: "是為了「那件事」來的吧？",
        style: "gradient",
        //滑到這一句，底部的按鈕就出現
        id: CTA_TRIGGER_ID,
      },
    ],
  },

  //9. 他捧著竹簡看過來。框裡三句是他的判語 ——
  //   跟 block 3 的「嗯…」一樣是說出口的話，所以同樣放在對話框裡，
  //   而不是做成黑底的文字 block（那是旁白的位置）。
  //
  //   這顆框的下緣落在圖高的 113%：刻意掛出圖外，讓它壓在下一塊的黑底上，
  //   像一張被放到畫面前面的紙。溢出能成立是因為 __bubble 有 z-index（見 scss）。
  {
    type: "image",
    src: `${IMG}baiye-story-5.jpg`,
    bubbles: [
      {
        text: "眉宇間\n沉積的陰霾\n騙不了人",
        //  85.5% 是用「整列亮度互相關」對出來的 ——
        //  不能靠肉眼找圖片上緣：這張圖的頂端有 10% 的 mask 淡出，
        //  第一列看得到顏色的地方，已經比元素真正的頂端低了一截。
        //  我第一版就是這樣抓的，結果框整個往上偏，壓到了他的手。
        top: "85.5%",
        left: "28.7%",
        width: "42.8%",
        //三行裡最長的是「沉積的陰霾」五個字 —— ratio 要遷就它，
        //  不是遷就第一行的三個字，不然那一行會頂破框。
        ratio: 0.135,
        aspect: "0.75",
      },
    ],
    //框的下緣落在圖高的 123%，也就是掛出去 23% 的圖高。
    //  換算成圖寬：23.1% ÷ 0.6584（這張圖的長寬比）≈ 35%。
    //  算式：框底 = top + 框高 = 85.5% + (42.8% ÷ 0.75 ÷ 1.5188) = 123.1%
    spill: "35%",
  },

  //10. 接在判語之後的兩句。第二句加重 ——
  //    前一句是問句（你在困惑什麼），後一句是他替你把答案說死，
  //    所以只有後面那句發光。
  {
    type: "text",
    size: "sm",
    //tail：這句說完就進命書了，中間要有一段空白當換場
    tail: true,
    lines: [
      "你在紅塵裡困惑什麼",
      //gap "sm"：問句問完停一下，但兩句還是連著的，不到另起一段的程度
      { text: "這張命盤早就為你寫好了", style: "strong", gap: "sm" },
    ],
  },

  //11. 命書面板。整頁第一次出現大段內文 ——
  //    前面都是一句一句的台詞，到這裡才真的開始「批命」。
  //
  //    bodyFrom 而不是把文字寫在這裡：這段是要用 AI 依命盤生成的，
  //    現在接的是佔位字串（見 SITUATION_PLACEHOLDER）。
  {
    type: "panel",
    bg: `${IMG}baiye-story-bg-1.jpg`,
    title: "你現在的狀況是",
    bodyFrom: "situation",
  },

  //12. 他把原因歸到命格上。前兩句是鋪陳（一般字重），
  //    第三句是結論：加粗、不發光（flat），並把「命格」兩個字挑出來換色 ——
  //    整段話的重量都壓在那兩個字上，所以整行只有它們亮。
  {
    type: "text",
    //格式比照 block 10（你在紅塵裡困惑什麼／這張命盤早就為你寫好了）——
    //  那兩塊是同一種語氣：前面鋪陳，最後一句加重把話說死。
    size: "sm",
    lines: [
      "你如今陷入這般境地",
      "並非偶然",
      {
        parts: ["全是你骨子裡的", { text: "命格" }, "在作祟"],
        //flat：加重但不發光。
        //  strong 本來帶一片往下沉的青光（見 scss 的 .is-strong），
        //  但這一行裡「命格」兩個字已經是薄荷綠、自己就是視覺重點 ——
        //  底下再墊一層青光會跟它撞色，兩個亮點在同一行搶。
        //  白字保留粗體當底，讓那兩個字獨自發亮。
        style: "strong flat",
        //這裡用 "lg"（1.6em）而不是像 block 10 那樣的 "sm"：
        //  前面是兩句鋪陳，停頓要夠久才撐得起「命格」這個結論。
        gap: "lg",
      },
    ],
  },

  //13. 命盤表。整頁第一次給「資料」而不是話術 ——
  //    前面講的都是他的判斷，這裡是排出來的盤，讓人自己看。
  //
  //    表的內容全部來自後端（from: "chart"），前端不做任何命理換算：
  //    十神、藏干、五行都是排盤的一部分，在這裡重算等於維護第二套實作。
  {
    type: "chart",
    bg: `${IMG}baiye-story-bg-2.jpg`,
    from: "chart",
  },

  //14. 看完盤之後他要下筆了。兩句都是一般白字（量設計稿是 30px、一般字重）——
  //    這裡不加重也不發光：前面剛給完一整張表，再上重音會蓋過那張表。
  {
    type: "text",
    size: "sm",
    //head：跟上面那張命盤表拉開，讓表先結束再開口
    head: true,
    //tail：說完這句才攤竹簡，下一張圖不要緊接著上來
    tail: true,
    lines: ["墨筆一落…", "前塵後事可就藏不住了"],
  },

  //15. 他攤開竹簡問你敢不敢看。兩顆對話框：
  //    左邊那顆是他攤簡時說的，右邊那顆是反問 —— 位置錯開、右邊那顆更低，
  //    讀起來才有先後，而不是同時講的兩句話。
  //
  //    右邊那顆整顆都在圖片之外（top 107%）。這是刻意的：
  //    竹簡在圖裡，問句落在圖外的黑底上，像他把話丟到畫面前面。
  {
    type: "image",
    src: `${IMG}baiye-story-6.jpg`,
    bubbles: [
      {
        text: "讓我來講講\n這因果",
        top: "53.3%",
        left: "12.1%",
        width: "39.4%",
        //字級 = 框寬 × ratio。框加寬時先調到 0.139 讓字維持原大小，
        //  再往回加到 0.148 —— 比原本大一點點，四周仍留得住白。
        ratio: 0.148,
        //只變寬不變高：高度 = 框寬 ÷ aspect，所以 aspect 要照同一個比例放大
        aspect: "0.734",
      },
      {
        text: "你\n受得住\n嗎？",
        top: "124%",
        left: "58.4%",
        width: "26.6%",
        ratio: 0.22,
        aspect: "0.734",
      },
    ],
    //兩顆框裡最低的是右邊那顆，下緣落在圖高的 176.8%。
    //  要讓出的高度 = (176.8% − 100%) 的圖高，換算成圖寬要除以圖的長寬比 1.458：
    //  76.8% ÷ 1.458 ≈ 53%。（percentage margin 一律以包含區塊的寬度為基準）
    //
    //  兩顆框縮放或上下移動時，這個數字都要跟著重算 ——
    //  掛出去那一截的長度會變，不改的話下面會多出（或少掉）一段空白。
    spill: "53%",
  },

  //16. 承上的旁白。最後一句用漸層字 ——
  //    量設計稿那一行由上到下從 (243,243,243) 過渡到 (199,205,206)，
  //    藍綠通道逐漸高於紅，就是 .is-gradient 那個白→青綠。
  {
    type: "text",
    size: "sm",
    //tail：問完才抬眼，下一張特寫不要緊接著上來
    tail: true,
    lines: [
      "凡人總是求一個真相",
      "可真擺在面前時",
      { text: "你又有膽量看嗎？", style: "gradient", gap: "lg" },
    ],
  },

  //17. 再一次的眼睛特寫，比 block 7 更近 ——
  //    問完「你又有膽量看嗎」之後，接的是他等你回答的那一眼。
  //    同樣不配任何文字：這一刻該安靜。
  { type: "image", src: `${IMG}baiye-story-7.jpg` },

  //18. 他開條件。三段語氣，中間用一條白線換場：
  //    前面是警告與質問（薄荷色，他在說話），
  //    後面是指令（粗體白字，他要你開口了）。
  //
  //    分隔線用白色而不是 block 8 那條青綠：那條是同一段話中間的停頓，
  //    這條是換場 —— 上下是兩種不同的語氣。
  {
    type: "text",
    size: "sm",
    lines: [
      "但是我只會給真相",
      "不給解藥",
      { text: "你，確定要聽？", style: "jade-strong", gap: true },
      { divider: "plain" },
      //一句比一句小、一句比一句淡 —— 他的聲音在退。
      //
      //  兩句都不發光：下面那句用 fade（漸層字，本來就不能有 text-shadow），
      //  上面這句要明確加 flat 才會一致 —— 一亮一暗的話，
      //  「聲音在退」會斷在第一句，讀起來像兩句不相干的話。
      { text: "現在一字一句", style: "strong lg flat" },
      { text: "來說看看吧", style: "strong fade md" },
    ],
  },

  //19. 章節大標：大運。整頁到這裡換段 ——
  //    前面是他跟你的對話，從這裡開始是一章一章的批命。
  //    所以版型也換：一條飾紋 + 兩個巨大的字，不講話。
  {
    type: "text",
    //tail "lg"：章節標題要四周空，不能讓下一張圖貼著「大運」兩個字
    tail: "lg",
    lines: [
      { ornament: `${IMG}baiye-story-bg-3.png` },
      //兩個字寫在同一筆裡（用 \n 分行）——
      //  淡出的漸層是套在「大運」整體上的，拆成兩行會各自套一次
      { title: "大\n運" },
    ],
  },

  //20. 進入大運章的第一段。兩顆框由上而下、由左而右 ——
  //    上面那顆是他起話頭（你的命運…），下面那顆才是結論，
  //    位置錯開才有「頓一下再說」的先後。
  {
    type: "image",
    src: `${IMG}baiye-story-8.png`,
    bubbles: [
      {
        text: "你的\n命運…",
        top: "42.5%",
        left: "9.8%",
        width: "29.6%",
        ratio: 0.205,
        aspect: "0.733",
      },
      {
        text: "即將進入一個\n重要轉換期",
        top: "72.8%",
        left: "40.3%",
        width: "48.7%",
        ratio: 0.128,
        aspect: "0.693",
        //量到這顆的光偏綠（ΔR −17 / ΔG +6 / ΔB +1），不是四柱那顆的青藍
        glow: "jade",
      },
    ],
    //下框的下緣落在圖高的 112.3%。
    //  換算成圖寬：12.3% × (1 ÷ 0.5625) ≈ 22%。
    //  兩顆框上下移動時這個數字要重算（同 block 15 的說明）。
    spill: "22%",
  },

  //21. 大運章的命書面板。結構同 block 11，差別只在內容與背景 ——
  //    標題兩行（用 \n 分行），內文一樣是之後要由 AI 生成的。
  {
    type: "panel",
    //head：上一塊那顆對話框的下緣剛好落在這裡，要讓開
    head: true,
    bg: `${IMG}baiye-story-bg-1.jpg`,
    title: "你的命運\n原來是這樣轉動的",
    bodyFrom: "daYun",
  },

  //22. 面板之後的收尾。第二句比第一句小、而且淡得更深 ——
  //    跟 block 18 的收尾是同一種手法（聲音在退），只是退得更遠。
  {
    type: "text",
    //上下都要留 —— 上面接命書面板、下面接下一章，兩邊都該喘一口
    head: true,
    tail: true,
    lines: [
      { text: "這符紙落了筆", style: "md" },
      { text: "牽動的可就是天機", style: "fade-dim sm" },
    ],
  },

  //23. 第二個章節大標：財運。結構與 block 19 的「大運」完全相同 ——
  //    同一條飾紋、同一種三層疊字，只換兩個字。
  //    章節標題長一樣是刻意的：它是這一頁的分隔記號，
  //    每章各設計一款的話，讀者就沒辦法一眼認出「又換章了」。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: `${IMG}baiye-story-bg-3.png` }, { title: "財\n運" }],
  },

  //24. 財運章的第一段。兩顆框，上輕下重 ——
  //    「說到底」是引子（小框、字大、一行），
  //    「財運如何」才是主題（大框、四個字排滿）。
  //
  //    下框的下緣落在圖高的 109%，掛出去 9% 的圖高 ——
  //    換算成圖寬是 9 × 1.78 ≈ 16%（這張圖的圖高是圖寬的 1.78 倍）。
  {
    type: "image",
    src: `${IMG}baiye-story-9.jpg`,
    bubbles: [
      {
        text: "說到底",
        top: "46.6%",
        left: "13%",
        width: "26.1%",
        ratio: 0.24,
        aspect: "0.645",
      },
      {
        text: "財運如何",
        top: "75.4%",
        left: "44.1%",
        width: "43%",
        ratio: 0.185,
        aspect: "0.718",
      },
    ],
    spill: "16%",
  },

  //25. 承上的反問。薄荷色粗體（量到 #abf8e1、筆畫 14%）——
  //    跟 block 18 的「你，確定要聽？」同一個樣式，
  //    都是他把問題丟回給你的時候。
  {
    type: "text",
    size: "sm",
    //head：上一塊那顆「財運如何」掛到圖外，這句要等它落定再出現
    head: true,
    //tail "lg"：問完要留一大拍，下一張特寫才不會貼上來
    tail: "lg",
    lines: [{ text: "才是你最想知道的吧？", style: "jade-strong md" }],
  },

  //26. 他低頭笑了的特寫。同 block 7／17，不配文字 ——
  //    反問之後的那個表情本身就是回答。
  { type: "image", src: `${IMG}baiye-story-10.jpg` },

  //27. 開始講過去。
  {
    type: "text",
    size: "sm",
    //head：跟上面那張特寫拉開，讓那個表情先停一下
    head: true,
    //tail "lg"：說完這句才翻開命書，中間留一大段空白當換場
    tail: "lg",
    lines: ["先來講講你的過去…"],
  },

  //28. 財運章的命書面板。結構同 block 11／21 ——
  //    每一章都是「大標 → 對話 → 面板」這個節奏，面板是那一章的正文。
  {
    type: "panel",
    bg: `${IMG}baiye-story-bg-1.jpg`,
    title: "你的財運\n曾是這樣流轉的",
    bodyFrom: "caiYunPast",
  },

  //29. 看完財運之後他下的結語。四行三種語氣：
  //    前兩句是評論（粗體白字、不發光），第三句收掉（淡出、字也小一點），
  //    停一大拍之後才是允諾（粗體白字 + 青藍光暈）。
  //
  //    整塊只有最後那句會發光，其餘三句都是平的 ——
  //    光在這一塊裡是「他給出承諾」的記號，不是加重的通用手段。
  //    前面也亮的話就分不出哪一句才是重點。
  //
  //    量到第三句從 171 起降 —— 比 .is-fade（221）暗、比 .is-fade-dim（138）亮，
  //    取比較接近的 fade-dim。
  {
    type: "text",
    //tail "lg"：這句允諾講完就換章了，中間要空出一段
    tail: "lg",
    lines: [
      //flat：加重但不發光（理由見上方）
      { text: "金銀銅臭雖是俗物", style: "strong flat" },
      { text: "但你這命盤裡的財氣", style: "strong flat" },
      //fade 而不是 fade-dim：這一句只是語氣收掉，不是消失 ——
      //  後面還有一句允諾要接，收得太深會讓中間斷開。
      //  （fade 本身就不能有 text-shadow，所以這句不必另外加 flat）
      { text: "可藏得真夠深的…", style: "strong fade md" },
      //整塊唯一發光的一句 —— 不加 flat 是刻意的
      { text: "我會教你怎麼把握機緣", style: "strong", gap: "lg" },
    ],
  },

  //30. 第三個章節大標：愛情。同 block 19（大運）、block 23（財運）。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: `${IMG}baiye-story-bg-3.png` }, { title: "愛\n情" }],
  },

  //31. 愛情章的第一段。兩顆框斜對角擺 ——
  //    上框在左上、下框在右下，視線從左上掃到右下，剛好經過他的臉。
  //
  //    上框的 top 是負的：它掛在圖片上緣之外，壓在上一塊（章節標題）
  //    的留白上。能這樣做是因為 __bubble 有 z-index（見 scss），
  //    而上一塊的 tail 本來就是黑的，框浮在上面看起來像貼在畫前面。
  {
    type: "image",
    src: `${IMG}baiye-story-11.png`,
    bubbles: [
      {
        text: "你真正\n命中注定\n的對象",
        top: "-9.2%",
        left: "6.7%",
        width: "35.4%",
        ratio: 0.168,
        aspect: "0.731",
      },
      {
        text: "天機\n早有刻痕",
        top: "69.3%",
        left: "54.1%",
        width: "42.7%",
        ratio: 0.168,
        aspect: "0.733",
        //量到 ΔR −19 / ΔG 0 / ΔB −5，跟四柱那顆完全相同 → 青藍那一版
        glow: true,
      },
    ],
    //下框的下緣落在圖高的 101.7%，只掛出去一點點：
    //  1.7% 的圖高 ÷ 0.5581 ≈ 3% 的圖寬。
    spill: "3%",
  },

  //32. 愛情章的命書面板。第四塊了，結構完全沒變 ——
  //    標題這次只有一行，用不用 \n 是內容的事，版型不受影響。
  {
    type: "panel",
    //head "sm"：上一塊的對話框只掛出去 3%，不需要 block 21 那種大留白
    head: "sm",
    bg: `${IMG}baiye-story-bg-1.jpg`,
    title: "我已瞧見那人的身影",
    bodyFrom: "aiQing",
  },

  //33. 面板之後的追問。第一句是鋪陳（淡出），
  //    後兩句把問題丟出來，各挑一個詞換成薄荷色 ——
  //    「劫」和「救贖」是這一整章的兩個答案，作法同 block 12 的「命格」。
  //
  //    後兩句都加 flat：理由同 block 12 —— 那兩個薄荷綠的字自己就是重點，
  //    白字底下再墊一層青光會跟它們撞色，一行裡兩個亮點在搶。
  {
    type: "text",
    //tail "xl"：問完「劫還是救贖」要停很久，他才把扇子舉起來
    tail: "xl",
    lines: [
      { text: "你心中已有一個人浮現出來", style: "strong fade-soft sm" },
      {
        parts: ["想知道他是你的", { text: "劫" }, "？"],
        style: "strong md flat",
        gap: "lg",
      },
      { parts: ["還是你的", { text: "救贖" }, "嗎？"], style: "strong md flat" },
    ],
  },

  //34. 他把扇子舉到臉前。框掛在圖片下緣之外一大截 ——
  //    橫式的圖只佔上面一條，框往下壓在黑底上，像話還沒說完。
  //
  //    註：這一塊的數值是估的，不是量設計稿得到的（那張參考圖沒有存檔）。
  {
    type: "image",
    src: `${IMG}baiye-story-12.jpg`,
    bubbles: [
      {
        text: "還有很多\n想跟你\n說的",
        top: "78%",
        left: "14.5%",
        width: "35.4%",
        ratio: 0.175,
        aspect: "0.75",
      },
    ],
    //框的下緣約落在圖高的 147%，掛出去 47% 的圖高。
    //  這張圖是橫式（比例 1.459），圖高只有圖寬的 0.69 倍，
    //  所以換算成圖寬是 47% × 0.69 ≈ 32%。
    spill: "32%",
  },

  //35. 接著把話收在「非凡命格」四個字上。
  //    兩句都 flat：理由同 block 12／33 —— 薄荷綠那四個字是這一段的落點，
  //    白字底下再墊青光會跟它們搶。
  {
    type: "text",
    //head：上一塊那顆框掛出圖外很多，這句要等它落定再出現
    head: true,
    //tail "lg"：這句收完才進字卡，中間要明確斷開
    tail: "lg",
    lines: [
      { text: "只屬於", style: "strong md flat" },
      {
        parts: ["你的", { text: "非凡命格" }, "…"],
        style: "strong md flat",
      },
    ],
  },

  //36. 字卡式的旁白，直接壓在畫面上（不是對話框）——
  //    這兩句不是他對你說的話，是這一頁在替他總結，
  //    所以不給氣泡，像電影的字卡橫在畫面中間。
  //
  //    量設計稿：字幕的上緣落在圖高的 54%，字 33px 粗體、置中。
  {
    type: "image",
    src: `${IMG}baiye-story-13.jpg`,
    captions: [
      {
        top: "54%",
        lines: [
          { text: "從你出生的那一刻", style: "strong" },
          { text: "到閉上雙眼的瞬間", style: "strong" },
        ],
      },
    ],
  },

  //37. 報告的份量。中間那條線比前面兩條都長 ——
  //    停得越久，線就越長（見 scss 的 &--jade）。
  {
    type: "text",
    //head：上一塊是滿版的字卡，這一塊要明確分開
    head: true,
    //tail "lg"：講完報告的份量才換到他的正面特寫
    tail: "lg",
    //整塊都不發光（flat）—— 前面剛過一張壓了字的圖，
    //  這裡再發光會讓兩塊黏在一起，分不出哪裡結束哪裡開始。
    lines: [
      { text: "描述你的五萬字報告", style: "strong md flat" },
      { text: "字字句句 皆為真實", style: "jade-strong sm flat" },
      { divider: "jade" },
      { text: "你的命格、你的大運", style: "strong sm flat" },
      { text: "你有所在意的那些事情", style: "strong sm flat" },
    ],
  },

  //38. 他正面看過來。框在右下，掛出圖外 12% 的圖高。
  {
    type: "image",
    src: `${IMG}baiye-story-14.png`,
    bubbles: [
      {
        text: "全藏在\n這份解析裡",
        top: "73.6%",
        left: "39.8%",
        width: "50.9%",
        ratio: 0.135,
        aspect: "0.734",
        //量到 ΔR −14 / ΔG +6 / ΔB 0（綠），而且上下對稱 → 不偏移的綠版
        glow: "jade-even",
      },
    ],
    //下緣落在圖高的 112.2% → 溢出 12.2% 的圖高。
    //  這張圖很瘦長（比例 0.5581，圖高是圖寬的 1.79 倍），
    //  換算成圖寬是 12.2 ÷ 0.5581 ≈ 22%。
    spill: "22%",
  },

  //39. 收束。前兩句平述，最後四個字用薄荷色壓下來。
  //
  //    tail "lg"：「顯露無遺」講完到第一章之間要空一大段。
  //    這裡是整頁最大的一次轉折 —— 前面都是他在跟你說話，
  //    下一塊開始是章節目錄（在預告報告裡有什麼）。
  //    兩塊貼著的話，那四個字會被下一塊的火焰背景接走，像同一段。
  //
  //    章節那一塊自己已經有 64px 的上內距，所以實際的縫是 90 + 64。
  {
    type: "text",
    size: "sm",
    tail: "lg",
    lines: [
      { text: "不必開口", style: "strong flat md" },
      { text: "早就透過這八字", style: "strong flat md" },
      //gap 用一般的（0.8em）而不是 "lg"：
      //  margin 是以自己的字級算的，而這一行是 35px、比上面兩句大 ——
      //  同樣寫 "lg" 在這裡會變成 56px，讀起來像換了一段。
      //  這四個字是接著「早就透過這八字」講完的，停一口氣就夠。
      { text: "顯露無遺", style: "jade-strong wide", gap: true },
    ],
  },

  //40. 章節目錄卡。這是整頁第一次「預告後面有什麼」——
  //    前面都在講他看到了什麼，這裡開始賣報告的內容。
  //
  //    跟命書面板的差別是不鋪黑底：那一塊要擋住背景好讀長文，
  //    這一塊要讓火焰透上來，字直接壓在畫面上。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    //不要在文案裡打空白：膠囊已經有 letter-spacing，
    //  兩份空隙疊起來會變成「第　一　章」那種過寬的排法
    badge: "第一章",
    title: "撕開命盤的偽裝吧",
    items: [
      { label: "天機總覽", text: "老天在你出生那刻寫下的秘密劇本" },
      { label: "五行暗湧", text: "決定你一生氣場與磁場的骨骼能量" },
      { label: "命格煞骨", text: "你骨子裡壓不住的狂傲與天賦" },
    ],
  },

  //41. 第二章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第二章",
    title: "連你自己都害怕面對的內心",
    items: [
      {
        label: "戴著面具的人間玩家",
        text: "外在的完美偽裝 vs. 骨子裡的厭世真相",
      },
      { label: "大腦運轉的詭譎軌跡", text: "你不可告人的思考邏輯與行動陰暗面" },
      { label: "當絕境降臨", text: "壓力逼到極限時，你會顯露的危險本性" },
    ],
  },

  //42. 第三章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第三章",
    title: "你在紅塵中的人性博弈",
    items: [
      { label: "人際戴面具指南", text: "你在社交場合中的防禦與捕食本能" },
      { label: "孽緣還是良緣", text: "你這體質究竟在瘋狂吸引哪些危險靈魂" },
      {
        label: "人際籌碼與致命盲點",
        text: "你以為的交情，其實隱藏著被背叛的風險",
      },
    ],
  },

  //43. 第四章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第四章",
    title: "事業這場局 你該怎麼破",
    items: [
      {
        label: "官運與野心",
        text: "你注定要爬上的高位，還是只甘心當個局外人",
      },
      {
        label: "生存地獄還是修羅場",
        text: "最能誘發你潛能（或把你逼瘋）的環境",
      },
      {
        label: "職涯必渡的劫數",
        text: "這條路上，究竟哪顆石頭會把你絆得血流成河",
      },
    ],
  },

  //44. 第五章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第五章",
    title: "你的暴富與破財密碼",
    items: [
      { label: "橫財與正財的獵場", text: "你該靠什麼門路讓錢自動送上門" },
      { label: "錢財破洞", text: "讓你不知不覺散盡家財的暗黑盲點" },
      { label: "聚寶盆開箱", text: "最適合你的吸金與擺脫貧窮翻身術" },
    ],
  },

  //45. 第六章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第六章",
    title: "你的情劫與執念",
    items: [
      {
        label: "渴望被愛又害怕被看穿",
        text: "你在深夜裡最真實的病態戀愛觀",
      },
      { label: "命中注定的情劫", text: "他何時渡海而來、何時與你擦肩" },
      { label: "致命吸引力", text: "你為什麼總是不知不覺淪陷在危險人物手裡" },
    ],
  },

  //46. 第七章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第七章",
    title: "你的氣血與壽元密碼",
    items: [
      { label: "肉身警報", text: "老天在你這具軀殼裡埋下的先天弱點" },
      {
        label: "被忽略的警訊",
        text: "你總以為是小毛病，實則是器官在發出的求救訊號",
      },
      { label: "續命退煞場", text: "最能為你調和五行、滋養元氣的養生法門" },
    ],
  },

  //47. 第八章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第八章",
    title: "你與原生家庭的因果清算",
    items: [
      {
        label: "父母是你的貴人還是情劫",
        text: "這輩子究竟是來還債，還是來報恩？",
      },
      { label: "壓得你喘不過氣的枷鎖", text: "原生家庭在你靈魂深處烙下的痛" },
      {
        label: "血脈裡的詛咒與恩澤",
        text: "如何從家庭的內耗中解脫，拿回屬於你的人生主導權",
      },
    ],
  },

  //48. 第九章，最後一章。
  {
    type: "chapter",
    bg: `${IMG}baiye-story-bg-4.jpg`,
    badge: "第九章",
    //tail：九章列完了，下一塊是他的收口，中間要斷開
    tail: true,
    title: "人生大局 這輩子你幾時翻身",
    items: [
      { label: "伏擊期", text: "那些看似沒起色、實則在暗中蓄力的蓄勢階段" },
      { label: "劇烈巨變", text: "哪一年你的命運會被徹底翻轉、翻江倒海？" },
      {
        label: "逆天改命的節奏",
        text: "踏準這幾個時機點，你就能徹底改寫宿命",
      },
    ],
  },

  //49. 九章列完之後的收口：他問你準備好了沒。
  {
    type: "image",
    src: `${IMG}baiye-story-15.png`,
    bubbles: [
      {
        text: "準備好\n看穿你半生的\n起伏走勢了\n嗎？",
        top: "72.6%",
        left: "40.4%",
        width: "48.4%",
        ratio: 0.125,
        aspect: "0.735",
        //量到 ΔR −15 / ΔG +7 / ΔB +1 → 綠光，且上下對稱
        glow: "jade-even",
      },
    ],
    //下緣落在圖高的 109.5% → 溢出 9.5% 的圖高。
    //  這張圖比例 0.5625（圖高是圖寬的 1.78 倍），換算成圖寬是 17%。
    spill: "17%",
  },

  //50. 進入用戶回饋段的標頭。第二行用漸層字 ——
  //    量到由上而下 255 → 168，而且藍綠通道逐漸高於紅。
  {
    type: "text",
    //head "lg"：上一塊那顆框掛出圖外 17%，這段標頭要等它落定再出現
    head: "lg",
    lines: [
      { text: "閻異閣-白夜", style: "strong sm" },
      { text: "真實用戶回饋", style: "gradient md" },
    ],
  },

  //51. 用戶回饋。信箱是遮罩過的，評語裡的換行照使用者原本打的斷。
  {
    type: "reviews",
    items: [
      {
        email: "aj****@gmail.com",
        stars: 5,
        text: "超準＝＝剛看第一頁我就毛骨悚然\n連我從小到大的隱密脾氣都被點出來 太扯了！",
      },
      {
        email: "dk*******@gmail.com",
        stars: 4,
        text: "太可怕了 真的很厲害 這是我看過最準的 🥹",
      },
      { email: "ss****@gmail.com", stars: 5, text: "準到發毛" },
      {
        email: "81******@gmail.com",
        stars: 5,
        text: "之前買過別家的都很籠統 這家的報告超級具體 講中我去年事業的大坑",
      },
      {
        email: "gr*****@gmail.com",
        stars: 5,
        text: "很多沒跟任何人講過的私事全部被寫出來\n看完直接起雞皮疙瘩 😱😱",
      },
      {
        email: "as*******@gmail.com",
        stars: 5,
        text: "很多事情是我完全沒告訴任何人只有在自己心裡想過的話 但他寫出來了\n第一次遇到這麼懂我的所以特地來評分！",
      },
      {
        email: "03*********@gmail.com",
        stars: 4,
        text: "原本只是隨便測測看\n結果連我最近在糾結的事都說中了…",
      },
      {
        email: "ke******@gmail.com",
        stars: 5,
        text: "講得非常透徹又有說服力\n看完突然看開很多事 真的拯救了最近超級迷茫的我 😂",
      },
    ],
  },
];

//對話框裡的字佔框寬的幾成（預設值）。
//  框放大之後字若照同一個比例跟著長，會把橢圓塞滿、少了留白 ——
//  0.29 讓「嗯…」在框裡浮著，四周留得開。
//
//  字多的框要自己覆寫（bubble.ratio）：同樣是一顆橢圓，
//  放兩個字和放四行三個字，能用的字級差一倍。
const BUBBLE_TEXT_RATIO = 0.29;

//把「框寬佔圖的百分比」換算成字級。
//  兩者都以整張圖為基準（container-type 在 .baiye-story__frame 上），
//  所以 15% 的框配 15 × 0.29 ≈ 4.4cqw 的字。
//  框改大小時字自動跟著，不必兩個數字一起調。
const bubbleFontSize = (width, ratio = BUBBLE_TEXT_RATIO) =>
  `${parseFloat(width) * ratio}cqw`;

//畫一行字。文字 block 與疊在圖上的字幕共用 ——
//  兩邊的一行都支援同樣的東西（style、gap、parts、from、divider、
//  ornament、title），抽出來才不會改了一邊忘了另一邊。
const renderLine = (line, i, data) => {
  //分隔線不是文字，不套 __line 那套字級與顏色 ——
  //  放在 lines 裡是因為它的位置就是「第幾行之後」，
  //  另外開一個欄位的話，改順序時兩邊都要記得動。
  //章節大標：三層同一組字疊在一起（左右暗分身 + 中間亮的）。
  //  分身用 aria-hidden：讀屏只該唸到一次「大運」。
  if (line.title)
    return (
      <span key={`title-${i}`} className="baiye-story__title">
        {["l", "r"].map((side) => (
          <span
            key={side}
            className={`baiye-story__title-layer baiye-story__title-layer--${side}`}
            aria-hidden="true"
          >
            {line.title}
          </span>
        ))}
        <span className="baiye-story__title-layer baiye-story__title-layer--core">
          {line.title}
        </span>
      </span>
    );

  //飾紋：一條橫的裝飾圖。跟 divider 一樣是「不是字，但位置是第幾行」
  if (line.ornament)
    return (
      <img
        key={`ornament-${i}`}
        className="baiye-story__ornament"
        src={line.ornament}
        alt=""
        loading="lazy"
      />
    );

  //divider: true 是預設（青綠），給字串就是變體（目前只有 "plain"）
  if (line.divider)
    return (
      <span
        key={`divider-${i}`}
        className={[
          "baiye-story__divider",
          line.divider !== true && `baiye-story__divider--${line.divider}`,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    );

  //每一行可以只給字串（一般白字），或給物件帶樣式。
  //  兩種寫法並存是刻意的 —— 大多數行都是白字，
  //  全部都寫成 { text: "…" } 只是讓表變難讀。
  const { text, style, gap, from } =
    typeof line === "string" ? { text: line } : line;

  //from 的行內容要等資料（暱稱、四柱），作法同對話框的 bubble.from。
  //  BLOCKS 是模組層的常數，元件拿到 intake 之前就建好了，
  //  所以表裡只能記「要哪一個欄位」，值在這裡才查得到。
  //parts：一行裡有局部換色的重點字（見 block 12）。
  //  拆成陣列而不是在字串裡塞標記（例如 **命格**）——
  //  那需要一個剖析器，而整份資料表只有這一種強調，不值得。
  const content = line.parts
    ? line.parts.map((part, j) =>
        typeof part === "string" ? (
          part
        ) : (
          <span className="baiye-story__mark" key={`${j}-${part.text}`}>
            {part.text}
          </span>
        ),
      )
    : from
      ? data?.[from]
      : text;

  return (
    <p
      key={from ? `${from}-${i}` : `${text || "parts"}-${i}`}
      //給底部按鈕當觸發點用（見 CTA_TRIGGER_ID）。
      //  用 id 而不是 ref：renderLine 是一般函式不是元件，
      //  要掛 ref 得把整條渲染路徑改成元件才行。
      id={line.id}
      className={[
        "baiye-story__line",
        //style 可以給多個，用空白分隔（例如 "strong lg"）——
        //  一行的「顏色與字重」和「大小」是兩件事，
        //  每一種組合各開一個 class 的話會變成排列組合。
        style &&
          style
            .split(" ")
            .map((n) => `is-${n}`)
            .join(" "),
        //和上一行之間空一段。gap: true 是一整段的停頓，
        //  gap: "sm" 是同一口氣裡鬆一點的換行。
        gap && (gap === true ? "has-gap" : `has-gap-${gap}`),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {content}
    </p>
  );
};

//畫一個 block。
//  版型判斷收在這裡，不寫進 BLOCKS 那張表 ——
//  表只描述「有什麼內容」，這裡才決定「長什麼樣」。
//
//  data 是「要等 API 才有的內容」（目前只有四柱）。
//  刻意用具名欄位查表（bubble.from）而不是把值直接塞進 BLOCKS：
//  BLOCKS 是模組層的常數，在元件還沒拿到 intake 之前就建好了。
const renderBlock = (block, index, data) => {
  const key = `${block.type}-${index}`;

  if (block.type === "image") {
    //一律包一層 frame：有對話框時它是定位基準，沒有時也統一結構 ——
    //  兩種寫法並存的話，接縫與 first-child 的樣式都要各寫一份。
    //框裡的對話框可以掛出圖片下緣（見 block 9）。溢出來的那一截是「浮」的，
    //  不佔版面高度，所以要自己把那段空間讓出來，不然會壓在下一塊上。
    //
    //  空間讓在這一塊的下緣、而不是叫下一塊多留上緣：
    //  那一截是這張圖的對話框造成的，之後把下一塊換掉或刪掉，
    //  它仍然需要同一段空間 —— 責任留在製造溢出的人身上才不會失聯。
    //
    //  百分比的 margin 是以「包含區塊的寬度」為基準（同 padding 那個坑），
    //  所以 spill 給的是「佔圖寬的百分比」，不是圖高。
    return (
      <div
        key={key}
        className="baiye-story__frame"
        style={block.spill ? { marginBottom: block.spill } : undefined}
      >
        <img
          className="baiye-story__image"
          src={block.src}
          alt=""
          //第一張要盡快出現（它就是第一屏），其餘等捲到附近再載
          loading={index === 0 ? "eager" : "lazy"}
        />

        {/* 疊在圖上的字幕：沒有框，整條橫跨、置中。
            位置只給 top —— 旁白的重點是「在畫面的第幾分之幾高」。 */}
        {(block.captions || []).map((caption, c) => (
          <div
            key={`caption-${c}`}
            className="baiye-story__caption"
            style={{ top: caption.top }}
          >
            {caption.lines.map((line, i) => renderLine(line, i, data))}
          </div>
        ))}

        {/* 一張圖可以有好幾顆對話框（block 15 有兩顆）。
            順序就是 DOM 順序，位置各自由資料給，彼此不影響。 */}
        {(block.bubbles || []).map((bubble, b) => (
          <div
            key={`bubble-${b}`}
            className={[
              "baiye-story__bubble",
              //光暈是特例不是通則：判語那顆量到完全沒有色偏。
              //  true 是青藍，給字串就是別的顏色（目前只有 "jade" 綠）。
              bubble.glow &&
                (bubble.glow === true
                  ? "baiye-story__bubble--glow"
                  : `baiye-story__bubble--glow-${bubble.glow}`),
            ]
              .filter(Boolean)
              .join(" ")}
            //位置與寬度是每張圖各自的，寫在資料裡而不是樣式裡。
            //  字級也設在這一層（不是裡面的 span）—— 框的內距用 em，
            //  而 em 只認元素自己的 font-size（見 scss 裡的 padding）。
            style={{
              top: bubble.top,
              left: bubble.left,
              width: bubble.width,
              fontSize: bubbleFontSize(bubble.width, bubble.ratio),
              //沒給就讓 scss 用預設的 7/10
              ...(bubble.aspect ? { "--bubble-aspect": bubble.aspect } : null),
            }}
          >
            {/* 固定文案直接給 text，要等 API 的給 from。
                還沒回來時橢圓先空著 —— 這時候放「載入中」反而更吵，
                框本身已經是畫面的一部分，突然冒出來才是閃動。 */}
            <span className="baiye-story__bubble-text">
              {bubble.from ? data?.[bubble.from] : bubble.text}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "chart") {
    const chart = data?.[block.from];
    //排盤還沒回來（或失敗）就整塊不畫 —— 空表格比沒有表格更像壞掉
    if (!chart?.columns?.length) return null;

    //藏干一柱最多三個。不足時用 null 補在「左邊」——
    //  這張表整體是由右往左讀的（年柱在最右），藏干也一樣：
    //  本氣要落在最右邊，所以陣列先反轉再往左補空格。
    const hidden = (list) => {
      const rev = [...list].reverse();
      return [...Array(Math.max(0, 3 - rev.length)).fill(null), ...rev];
    };

    const cols = chart.columns;

    return (
      <section
        key={key}
        className="baiye-story__chart"
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="baiye-story__chart-inner">
          {data?.name && (
            <h2 className="baiye-story__chart-title">
              {data.name} 的命局
              {data.birthDate && (
                <span className="baiye-story__chart-date">
                  {data.birthDate.replace(
                    /^(\d+)\/(\d+)\/(\d+)$/,
                    "$1年 $2月 $3日",
                  )}
                </span>
              )}
            </h2>
          )}

          {/* 欄數寫成 CSS 變數：不知時辰的人只有三柱，寫死 4 會破版 */}
          <div
            className="baiye-story__chart-grid"
            style={{ "--cols": cols.length }}
          >
            {/* 天干的十神。
                刻意沒有欄標題列（時柱／日柱…）也沒有列標題欄（十神／天干…）——
                四柱表的欄列順序是固定的讀法，看得懂的人不需要標籤，
                而標籤會把一張只有八個字的表撐成一張報表。 */}
            {cols.map((c) => (
              <div className="baiye-story__chart-cell" key={`g-${c.label}`}>
                {c.god}
              </div>
            ))}

            {/* 天干 / 地支：兩列結構一樣，只差取哪個欄位 */}
            {["gan", "zhi"].map((key) => (
              <Fragment key={key}>
                {cols.map((c) => (
                  <div
                    className={`baiye-story__chart-cell baiye-story__chart-glyph baiye-story__chart-glyph--${c[key].element}`}
                    key={`${key}-${c.label}`}
                  >
                    {c[key].char}
                  </div>
                ))}
              </Fragment>
            ))}

            {/* 藏干，以及它們各自的十神。
                十神是直書的（兩個字上下疊）—— 一柱最多三個十神並排，
                橫著寫的話六個字會擠成一長條，分不出哪兩個字是一組。 */}
            {["char", "god"].map((field) => (
              <Fragment key={field}>
                {cols.map((c) => (
                  <div
                    className="baiye-story__chart-hidden"
                    key={`${field}-${c.label}`}
                  >
                    {hidden(c.hidden).map((h, i) => (
                      <span
                        className={[
                          "baiye-story__chart-hidden-cell",
                          field === "god"
                            ? "baiye-story__chart-hidden-cell--god"
                            : null,
                          h && field === "char"
                            ? `baiye-story__chart-hidden-cell--${h.element}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={`${c.label}-${field}-${i}`}
                      >
                        {h ? h[field] : ""}
                      </span>
                    ))}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (block.type === "reviews") {
    return (
      <section key={key} className="baiye-story__reviews">
        {block.items.map((review, i) => (
          <article
            className="baiye-story__review"
            key={`${i}-${review.email}`}
          >
            <p className="baiye-story__review-email">{review.email}</p>
            {/* 星星用字元重複而不是圖示：它要跟文字一起縮放、一起繼承顏色。
                aria-label 才是給讀屏的，★ 本身標成 aria-hidden，
                不然會被唸成五次「黑星」。 */}
            <p
              className="baiye-story__review-stars"
              aria-label={`${review.stars} 顆星`}
            >
              <span aria-hidden="true">{"★".repeat(review.stars)}</span>
            </p>
            <p className="baiye-story__review-text">{review.text}</p>
          </article>
        ))}
      </section>
    );
  }

  if (block.type === "chapter") {
    return (
      <section
        key={key}
        className={[
          "baiye-story__chapter",
          //跟後面那一塊拉開（章與章之間另有 & + & 管）
          block.tail && "baiye-story__chapter--tail",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="baiye-story__chapter-inner">
          {block.badge && (
            <p className="baiye-story__chapter-badge">{block.badge}</p>
          )}
          {block.title && (
            <h2 className="baiye-story__chapter-title">{block.title}</h2>
          )}

          {(block.items || []).map((item, i) => (
            <p className="baiye-story__chapter-item" key={`${i}-${item.label}`}>
              <span className="baiye-story__chapter-mark" aria-hidden="true">
                ◆
              </span>
              {item.label}：{item.text}
            </p>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === "panel") {
    //內容可以是陣列（一段一筆）或一整串換行分隔的字。
    //  容忍兩種是因為 AI 回來的是 string[]，但寫死在表裡的可能是一坨文字。
    const body = block.bodyFrom ? data?.[block.bodyFrom] : block.body;
    const paragraphs = Array.isArray(body)
      ? body
      : String(body || "")
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean);

    //沒有內文就整塊不掛。
    //  掛一個只有標題、底下一片空白的黑卡會比少掉這一塊更像壞掉 ——
    //  而這一頁是連續往下捲的長圖，中間少四塊看不出來。
    //
    //  這同時涵蓋兩種情況：AI 還在路上、以及 AI 生成失敗。
    //  使用者分不出差別，所以不必分開處理。
    if (!paragraphs.length) return null;

    return (
      <section
        key={key}
        className={[
          "baiye-story__panel",
          //跟文字 block 同名的旋鈕：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "sm"）。
          block.head &&
            (block.head === true
              ? "baiye-story__panel--head"
              : `baiye-story__panel--head-${block.head}`),
        ]
          .filter(Boolean)
          .join(" ")}
        //背景圖寫在 inline style：哪一塊用哪張圖是內容的事，不是樣式的事。
        //  用 CSS 變數而不是直接設 backgroundImage —— 花紋實際畫在 ::before 上
        //  （那一層才做上下淡出），而偽元素沒辦法用 inline style 指定。
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="baiye-story__panel-card">
          {block.title && (
            <h2 className="baiye-story__panel-title">{block.title}</h2>
          )}

          {paragraphs.map((text, i) => (
            <p
              className="baiye-story__panel-text"
              key={`${i}-${text.slice(0, 8)}`}
            >
              {text}
            </p>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === "text") {
    return (
      <div
        key={key}
        className={[
          "baiye-story__text-block",
          //放大是整段的事，所以掛在 block 上而不是逐行掛 ——
          //  掛在行上的話，同一段裡加一行就得記得也標一次。
          block.size && `baiye-story__text-block--${block.size}`,
          //講完要停一下的段落：下緣多留一大截，下一塊圖才不會緊接著上來。
          //  tail: true 是預設，給字串就是分級（目前只有 "lg"）——
          //  作法同行距的 gap，級距集中在 scss 裡，資料表只說「要哪一級」。
          block.tail &&
            (block.tail === true
              ? "baiye-story__text-block--tail"
              : `baiye-story__text-block--tail-${block.tail}`),
          //重新開口的段落：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "lg"）。
          block.head &&
            (block.head === true
              ? "baiye-story__text-block--head"
              : `baiye-story__text-block--head-${block.head}`),
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {block.lines.map((line, i) => renderLine(line, i, data))}
      </div>
    );
  }

  return null;
};

const BaiyeStoryPage = () => {
  //—— 動線打點：進故事頁、讀到底 ——
  //
  //  「讀到底」用捲動判斷，不在 JSX 裡放哨兵元素 ——
  //  這一頁的內容是 BLOCKS 資料表畫出來的，塞一個跟內容無關的收尾節點
  //  會讓那張表之外多出一個要記得維護的東西。
  //
  //  打完就把監聽拿掉：捲動事件在手機上一秒觸發幾十次，而這件事
  //  只需要知道一次。留 120px 的餘裕 —— 行動瀏覽器的網址列收合會讓
  //  底部差個幾十 px，要求剛好捲到底的話很多人永遠觸發不了。
  useEffect(() => {
    track("story_view", "bazi-baiye");

    const onScroll = () => {
      const el = document.documentElement;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 120) return;
      track("story_end", "bazi-baiye");
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navigate = useNavigate();
  //捲動容器。按鈕的觸發判斷要以它為基準 ——
  //  這一頁自己捲（見 scss 的 .baiye-story），不是整個視窗在捲，
  //  IntersectionObserver 的 root 給錯就永遠不會觸發。
  const scrollRef = useRef(null);
  //付款輪詢的計時器。放 ref 是為了離開這一頁時清掉 ——
  //  留著的話它會在元件卸載之後繼續打 API，而且永遠不會停。
  const pollRef = useRef(null);
  const [showCta, setShowCta] = useState(false);
  const [buying, setBuying] = useState(false);

  const { state, search } = useLocation();

  const intake =
    state?.intake || (wantsSampleIntake(search) ? SAMPLE_INTAKE : null);

  //—— 購買流程 ——
  //
  //  購買 sheet（選方案）→ 發票 sheet（載具／統編）→ 建單 → 跳轉綠界。
  //  中間不擋登入：這是單次消費的商品，使用者剛走完十二題問診，
  //  在這裡擋一道登入牆是會掉單的。身分靠發票填的那個信箱
  //  （見後端 P-4b 與 models/order 的 email 欄位說明）。
  //
  //  方案跟後端拿，不寫死在前端 —— 建單時算的金額必須跟畫面上顯示的
  //  是同一份。兩邊各存一份價格，遲早會出現「畫面寫一個數、實際扣另一個」。
  const [plans, setPlans] = useState([]);
  //選完方案、等著填發票的那一份。null 代表發票 sheet 沒開。
  const [pendingPlan, setPendingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    PaymentService.plans("report")
      .then(({ data }) => {
        if (!data?.status) return;
        //後端回的是 { id, name, summary, price, original, discount }，
        //  PurchaseSheet 要的是 { id, title, note, price, original } ——
        //  在這裡轉，不要去改那支共用元件的介面（提問頁也在用它）。
        //
        //  ⚠️ 要先濾掉別支產品的方案。/plans?type=report 回的是**所有**
        //  報告類方案（現在有白夜與鏡月兩支），不濾的話白夜的購買視窗
        //  會同時列出兩份，使用者可以在這一頁買到另一支產品。
        //  濾的是方案 id，對應 config/product-plans.js 的鍵。
        setPlans(
          (data.data?.plans || [])
            .filter((p) => p.id === "baiye")
            .map((p) => ({
              id: p.id,
              title: p.name,
              note: p.summary,
              price: p.price,
              original: p.original,
            })),
        );
      })
      .catch(() => {
        //拿不到方案時購買 sheet 會是空的。不另外跳錯誤 ——
        //  使用者還在看故事，這時彈一個「載入失敗」只是打斷他。
        //  真的按下購買才會發現，那時 PurchaseSheet 自己會顯示沒有方案。
      });
  }, []);

  //購買 sheet 按下「繼續」：收起它、打開發票 sheet
  const handlePickPlan = useCallback(({ plan }) => {
    setPayError("");
    setPendingPlan(plan);
    setBuying(false);
  }, []);

  //—— 盯著訂單，等它變成已付款 ——
  //
  //  綠界開在新分頁之後，這一頁就與付款流程脫鉤了：
  //  付款結果是綠界 server-to-server 送到後端的，瀏覽器收不到任何通知，
  //  而導回 /purchase-result 的是「那一個分頁」，不是這一頁。
  //  所以這一頁只能自己問後端：這張單付了沒。
  //
  //  用輪詢而不是等分頁回報（postMessage / 監聽關閉）：
  //  使用者可能直接把綠界那頁關掉、可能在手機上切走再也沒切回來，
  //  那些情況下回報永遠不會來，但訂單其實已經付掉了。
  //  問後端是唯一不依賴使用者行為的做法。
  const watchOrder = useCallback(
    (orderID) => {
      if (!orderID) return;
      clearInterval(pollRef.current);

      const started = Date.now();
      pollRef.current = setInterval(() => {
        //超過上限就停。不設上限的話，使用者放著不管就是一個
        //  每三秒打一次、永遠不會停的請求迴圈。
        if (Date.now() - started > PAY_POLL_LIMIT_MS) {
          clearInterval(pollRef.current);
          return;
        }

        PaymentService.orderStatus(orderID)
          .then(({ data }) => {
            const status = data?.data?.status;
            if (process.env.NODE_ENV === "development") {
              console.log(`[ECPay] 查單 ${orderID} → ${status || "查無"}`);
            }
            if (status !== "paid") return;
            clearInterval(pollRef.current);
            //付掉了就把這一頁也帶到結果頁 —— 綠界那個分頁已經在那裡了，
            //  兩邊一致，使用者關掉哪一個都不會看到一個停在半路的畫面。
            navigate(`/purchase-result?orderID=${orderID}`, { replace: true });
          })
          //查單失敗不中斷輪詢：可能只是網路斷一下，下一輪就回來了
          .catch(() => {});
      }, PAY_POLL_MS);
    },
    [navigate],
  );

  //離開這一頁就停掉輪詢
  useEffect(() => () => clearInterval(pollRef.current), []);

  //發票填完：建單並跳轉綠界
  const handleCheckout = useCallback(
    (invoice) => {
      if (!pendingPlan || submitting) return;
      setSubmitting(true);
      setPayError("");

      //分頁要在這裡開 —— 這一行還在使用者按下按鈕的那個事件裡。
      //  等建單的 API 回來才開會被瀏覽器當成彈窗擋掉（理由見 ecpay-redirect）。
      const win = openEcpayWindow();

      //—— 動線打點：送出結帳 ——
      //  打在真的要建單之前。最後一段（付款成功）不打點 ——
      //  綠界導回時使用者可能已經關掉分頁，那個數字要從訂單來
      //  （見 config/funnel.js 的說明）。
      track("checkout_submit", "bazi-baiye");
      PaymentService.checkoutGuest(pendingPlan.id, invoice, intake)
        .then(({ data }) => {
          if (!data?.status) {
            setPayError(data?.message || "建立訂單失敗");
            win?.close();
            setSubmitting(false);
            return;
          }

          //—— 測試用：把這一單的編號印出來 ——
          //  綠界的付款結果是 server-to-server 送到後端的，瀏覽器永遠看不到，
          //  所以這裡只能給編號，讓人拿它去對後端 log 或查訂單狀態。
          //  正式環境不印：orderID 出現在 console 等於多一個外洩管道。
          if (process.env.NODE_ENV === "development") {
            const id = data.data.params?.MerchantTradeNo;
            console.log(
              `[ECPay] 建單成功 orderID=${id}\n` +
                `  綠界通知會打到後端，看 server 的 [ECPay] log\n` +
                `  或查：curl -s http://localhost:8080/api/payment/order-status/${id}`,
              data.data.params,
            );
          }

          submitToEcpay(data.data.action, data.data.params, win);
          //開在新分頁的話這一頁還在，submitting 要解除 ——
          //  不解除的話使用者在綠界那邊取消、切回來會發現按鈕永遠是暗的。
          if (win) {
            setSubmitting(false);
            watchOrder(data.data.params?.MerchantTradeNo);
          }
        })
        .catch((e) => {
          win?.close();
          setPayError(
            e?.response?.data?.message || "建立訂單失敗，請稍後再試",
          );
          setSubmitting(false);
        });
    },
    [pendingPlan, submitting, intake, watchOrder],
  );
  //沒帶 state 時：開發中或網址帶 ?preview 就用範例資料頂著，
  //  其餘情況維持原本行為 —— 沒有問診資料就導回劇情頁。


  //—— 四柱 ——
  //  排盤在後端（見 general-route 的 G-3）：四柱牽涉立春換年、節氣換月、
  //  子時跨日與真太陽時校正，而且畫面上這四柱必須跟之後報告裡的完全一致，
  //  所以跟報告共用同一支 getPillars，前端只負責顯示。
  //
  //  失敗就讓框空著，不擋流程 —— 這一格是氣氛，不是必要資訊，
  //  真正會用到四柱的是購買後那份報告，那時後端會自己重算一次。
  const [pillars, setPillars] = useState("");
  //命盤表的資料（十神／天干／地支／藏干）。跟四柱同一支 API 回來的。
  const [chart, setChart] = useState(null);

  useEffect(() => {
    if (!intake) return undefined;

    //元件在請求回來前就被卸載（使用者按了返回）時，不要再 setState
    let alive = true;

    generalService
      .baziPillars({
        birthDate: intake.birthDate,
        birthTime: intake.birthTime,
        birthPlace: intake.birthPlace,
        gender: intake.gender,
      })
      .then(({ data }) => {
        if (!alive || !data?.status) return;
        //一柱一行，靠 .baiye-story__bubble-text 的 pre 斷行
        setPillars((data.data?.pillars || []).join("\n"));
        setChart(data.data || null);
      })
      .catch((e) => console.warn("[白夜] 四柱取得失敗", e?.message));

    return () => {
      alive = false;
    };
  }, [intake]);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  請求不是在這裡發的，是劇情頁在問診倒數第二題就發了（見 baiye-teaser-cache）。
  //  這裡只是去把那個還在路上的 Promise 接回來 —— 從發出到使用者滑到
  //  第一塊面板，中間隔著兩支影片與整頁 15% 的捲動，約三十秒。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面是一樣的
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  因為使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  //開場那兩支影片播完了沒。沒播完就不讓底下的長捲頁出現 ——
  //  影片是蓋在上面的（fixed），但底下的頁面還是會吃捲動，
  //  不擋的話使用者可以一邊看影片一邊把故事滑掉。
  const [introDone, setIntroDone] = useState(false);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  —— 為什麼是「按下開始解命」才發，不是一掛載就發 ——
  //  這支生成是付費前的成本：每個走到這裡的人都要燒一次，買不買都一樣。
  //  掛載就發的話，在第一支影片（9.5 秒）中途關掉頁面的人也會被算一次，
  //  那是純浪費。「按下開始解命」是一個明確的互動，意願訊號強得多。
  //
  //  時間還夠不夠：按下去之後還有 baiye-5（他批命 8 秒，不能跳過），
  //  加上要滑到整頁的 15% 才看得到第一塊面板（10-20 秒）——
  //  掩護 18 到 28 秒，而生成實測 6 秒。綽綽有餘。
  //
  //  startTeaser 是單發的：同一份問診資料只會真的打一次
  //  （React 嚴格模式會讓事件處理器以外的東西跑兩遍，見 baiye-teaser-cache）。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面一樣
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  const [panels, setPanels] = useState(null);

  //開場那顆「開始解命」按下去時由開場元件回呼。
  //  aliveRef 而不是 effect 的區域變數：這不是 effect，是事件處理器 ——
  //  使用者可能在生成回來之前就按返回離開，那時 setPanels 會對著
  //  已卸載的元件呼叫。
  //
  //  ⚠️ 進場時一定要把它設回 true，不能只靠 useRef(true) 的初始值：
  //  嚴格模式下 effect 會跑「掛載 → cleanup → 再掛載」，
  //  cleanup 把它翻成 false，而第二次掛載不會重新執行 useRef 的初始值 ——
  //  結果就是從頭到尾都是 false，setPanels 永遠不會被呼叫，
  //  四塊面板明明生成成功卻一塊都不出現。
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const beginTeaser = useCallback(() => {
    if (!intake) return;
    startTeaser(intake).then((data) => {
      if (aliveRef.current) setPanels(data);
    });
  }, [intake]);

  //—— 底部按鈕的出現時機 ——
  //  觸發之後就 disconnect：按鈕只出現一次，往回捲不會又消失。
  //
  //  ⚠️ 一定要等開場播完才掛這個 observer。兩個理由，第二個是會出錯的那個：
  //
  //  1. 開場期間整頁被影片蓋著也不能捲，這時候讓按鈕冒出來沒有意義。
  //
  //  2. 頁面剛掛載時那十九張圖都還沒有高度，整頁只有幾百 px ——
  //     觸發點最終在 2653px，但那一瞬間它就在視窗裡。observer 會立刻
  //     判定「滑到了」然後 disconnect，按鈕就從此固定在畫面上。
  //     這個 bug 只有在圖沒進快取時才會出現（冷啟動、第一次進站的真實使用者），
  //     開發時重整幾次之後反而看不到 —— 所以它很容易被漏掉。
  //
  //     等開場那十七秒過去，圖早就載完、版面也定了，量到的位置才是真的。
  useEffect(() => {
    if (!intake || !introDone) return undefined;

    const target = document.getElementById(CTA_TRIGGER_ID);
    const root = scrollRef.current;
    if (!target || !root) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShowCta(true);
        io.disconnect();
      },
      { root },
    );
    io.observe(target);

    return () => io.disconnect();
  }, [intake, introDone]);

  //沒有問診資料就不該停在這一頁（直接貼網址、或重新整理）。
  //  replace 而不是 push：這一頁不該留在返回堆疊裡，
  //  不然使用者回上一頁又會被彈回來一次。
  useEffect(() => {
    if (!intake) navigate(BACK_TO, { replace: true });
  }, [intake, navigate]);

  //返回：有上一頁就回上一頁，沒有就回劇情頁。
  //  直接貼網址進來時 history 沒有上一頁，navigate(-1) 會把人送出本站
  //  （退路判斷同 sub-header 與劇情頁）。
  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(BACK_TO, { replace: true });
  };

  //導頁是在 effect 裡做的（render 當下還不能導），所以這一幀先不畫東西 ——
  //  畫了會閃一下沒有前提的空頁面。
  if (!intake) return null;

  return (
    <div
      className={`baiye-story${introDone ? "" : " is-intro"}`}
      ref={scrollRef}
    >
      {/* 開場：他看完竹冊、然後批命。這兩幕原本在劇情頁，
          搬過來是為了把生成那四塊命書的時間藏在劇情底下（見上方 panels 那段）。
          播完就卸載 —— 留著的話那兩支影片會一直佔著記憶體與解碼器，
          iOS 上同時掛太多 <video> 會讓後面的影片播不出來。 */}
      {!introDone && (
        <BaiyeStoryIntro
          onReading={beginTeaser}
          onDone={() => setIntroDone(true)}
        />
      )}
      {/* 返回鍵浮在內容之上。不用站內的 SubHeader ——
          那是一條有底色的頂部列，會把這一頁從「戲的延續」拉回「網站的次層頁」。 */}
      <button
        type="button"
        className="baiye-story__back"
        onClick={handleBack}
        aria-label="返回"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M15 5l-7 7 7 7"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* 一個個 block 串下去。中間沒有間隙 ——
          圖和圖、圖和文之間要接得像同一張長圖，不是一段段的卡片。
          結尾的行動按鈕等內容定案再加，現在整頁就是故事本身。 */}
      <main className="baiye-story__blocks">
        {BLOCKS.map((block, i) =>
          renderBlock(block, i, {
            pillars,
            chart,
            name: intake.name,
            birthDate: intake.birthDate,
            //四塊命書的內文。panels 還沒回來（或失敗）時是 undefined，
            //  那四塊就不會掛上來 —— 見上方 panels 那段說明。
            situation: panels?.situation,
            daYun: panels?.daYun,
            caiYunPast: panels?.caiYunPast,
            aiQing: panels?.aiQing,
          }),
        )}
      </main>

      {/* 固定在底部的行動按鈕。外層是全站共用的 .app-fixed ——
          它貼著欄位而不是視窗邊緣，桌機上才不會跑到畫面最旁邊。 */}
      {showCta && (
        <div className="app-fixed baiye-story__cta-layer">
          {/* 按鈕上的字已經畫在圖裡，所以不放文字節點。
              aria-label 仍然要給 —— 讀屏看不到圖上的字，
              沒有它這顆按鈕會被唸成「按鈕」而已。 */}
          <button
            type="button"
            className="baiye-story__cta"
            aria-label="向白夜窺探命局"
            style={{ "--cta-bg": `url(${IMG}baiye-story-btn.png)` }}
            onClick={() => {
              //—— 動線打點：開啟購買視窗 ——
              track("checkout_open", "bazi-baiye");
              setBuying(true);
            }}
          />
        </div>
      )}

      <PurchaseSheet
        open={buying}
        plans={plans}
        onClose={() => setBuying(false)}
        onSubmit={handlePickPlan}
      />

      {/* 發票資訊。在跳轉綠界之前問 ——
          電子發票是法規要求，而綠界付完款不會再問一次。
          預設信箱帶問診填的那個：這個流程沒有登入，抓不到 Google 信箱。 */}
      {pendingPlan && (
        <InvoiceSheet
          open
          amount={pendingPlan.price}
          defaultEmail={intake.email}
          defaultName={intake.name}
          submitting={submitting}
          onClose={() => {
            setPendingPlan(null);
            setPayError("");
          }}
          onConfirm={handleCheckout}
        />
      )}

      {/* 建單失敗。用浮動提示而不是換頁 ——
          使用者還在故事頁上，把他導走等於要他從頭再來一次。 */}
      {payError && (
        <div className="baiye-story__pay-error" role="alert">
          {payError}
        </div>
      )}
    </div>
  );
};

export default BaiyeStoryPage;
